import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { JudgeDataContext } from 'data/JudgeDataProvider';

type TestJudgeTabletProps = {
  className?: string;
  onTestPassed?: () => void;
};

const TEST_NOTE = 'TABLET TEST - DELETE ME';

const getContestantId = (contestant: any): number =>
  Number(
    contestant?.id ??
      contestant?.registeredContestantId ??
      contestant?.registeredPageantContestantId ??
      0,
  );

const getAocId = (row: any): number =>
  Number(row?.aocId ?? row?.aOCId ?? row?.AOCId ?? row?.pageantAvailableEventId ?? 0);

const getAocScoreCategoryId = (row: any): number =>
  Number(
    row?.aocScoreCategoryId ??
      row?.aOCScoreCategoryId ??
      row?.AOCScoreCategoryId ??
      row?.scoreCategoryId ??
      0,
  );

const getJudgeId = (row: any): number =>
  Number(row?.judgeId ?? row?.JudgeId ?? row?.eventJudgeId ?? row?.EventJudgeId ?? 0);

const getAocCategories = (aoc: any): any[] =>
  aoc?.aocScoreCategories ?? aoc?.aocScoringCategories ?? aoc?.scoreCategories ?? [];

const getRowsForContestant = (allFetchedScores: any, contestantId: number): any[] =>
  allFetchedScores?.[Number(contestantId)] ??
  allFetchedScores?.[String(contestantId)] ??
  [];

const TestJudgeTablet: React.FC<TestJudgeTabletProps> = ({
  className = '',
  onTestPassed,
}) => {
  const {
    judgeId,
    pageantAvailableAOC = [],
    registeredContestants = [],
    allFetchedScores = {},
    saveScores,
    refetchReceivedScores,
    isEnteredForAOC,
  } = useContext(JudgeDataContext);

  const [isTesting, setIsTesting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [variant, setVariant] = useState<'success' | 'danger' | 'warning' | 'info'>('info');
  const [hasPassed, setHasPassed] = useState(false);



  const hasExistingScoreForJudgeContestantAoc = useCallback(
    (contestantId: number, testAocId: number, currentJudgeId: number): boolean => {
      const rows = getRowsForContestant(allFetchedScores, contestantId);

      return rows.some((row: any) => {
        const scoreValue = row?.score ?? row?.Score ?? row?.receivedScore ?? null;

        return (
          getJudgeId(row) === currentJudgeId &&
          getAocId(row) === testAocId &&
          scoreValue !== null &&
          scoreValue !== undefined &&
          scoreValue !== ''
        );
      });
    },
    [allFetchedScores],
  );

  const handleRunTabletTest = useCallback(async () => {
    try {
      setIsTesting(true);
      setMessage(null);
      setVariant('info');

      const currentJudgeId = Number(judgeId ?? 0);

      if (!currentJudgeId) {
        setVariant('warning');
        setMessage('Missing judge information.');
        return;
      }

      const testAoc = pageantAvailableAOC.find((aoc: any) => {
        return getAocCategories(aoc).length > 0;
      });

      if (!testAoc) {
        setVariant('warning');
        setMessage('No scoring category was found to test.');
        return;
      }

      const testAocId = Number(testAoc?.id ?? 0);
      const testCategory = getAocCategories(testAoc)[0];
      const testCategoryId = Number(testCategory?.id ?? 0);

      if (!testAocId || !testCategoryId) {
        setVariant('warning');
        setMessage('No valid AOC/category was found to test.');
        return;
      }

      const testContestant = registeredContestants.find((contestant: any) =>
        isEnteredForAOC(contestant, testAocId),
      );

      if (!testContestant) {
        setVariant('warning');
        setMessage('No contestant entered in this AOC was found to test.');
        return;
      }

      const contestantId = getContestantId(testContestant);

      if (!contestantId) {
        setVariant('warning');
        setMessage('Missing contestant information.');
        return;
      }

      if (hasExistingScoreForJudgeContestantAoc(contestantId, testAocId, currentJudgeId)) {
        setVariant('danger');
        setMessage('Test has been canceled, a test score has already been recorded.');
        return;
      }

      await saveScores([
        {
          id: 0,
          aocScoreCategoryId: testCategoryId,
          registeredPageantContestantId: contestantId,
          judgeId: currentJudgeId,
          score: 1,
          notes: TEST_NOTE,
          dateReceived: new Date().toISOString(),
          aocId: testAocId,
        },
      ]);

      await refetchReceivedScores({
        registeredPageantContestantId: contestantId,
      });

      setVariant('success');
      setMessage('Tablet test passed. Test score reached the tabulator.');
      setHasPassed(true);
      onTestPassed?.();
    } catch (error) {
      console.error('[TestJudgeTablet] Tablet test failed:', error);
      setVariant('danger');
      setMessage('Tablet test failed. Please contact the tabulator.');
    } finally {
      setIsTesting(false);
    }
  }, [
    judgeId,
    pageantAvailableAOC,
    registeredContestants,
    saveScores,
    refetchReceivedScores,
    isEnteredForAOC,
    hasExistingScoreForJudgeContestantAoc,
    onTestPassed,
  ]);

  return (
    <div className={className}>
      {!hasPassed ? (
        <Button
          variant="outline-danger"
          className='btn-lg text-large w-100'
          onClick={handleRunTabletTest}
          disabled={isTesting}
        >
          {isTesting ? 'Testing Tablet...' : 'Test Tablet Signal'}
        </Button>
      ) : null}

      {message ? (
        <Alert variant={variant} className="mt-2 mb-0 py-2 small">
          {message}
        </Alert>
      ) : null}
    </div>
  );
};

export default TestJudgeTablet;